import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppShell } from "@/layout/AppShell";
import { LoginPage } from "@/features/auth/LoginPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { ClientsPage } from "@/features/clients/ClientsPage";
import { ClientDetailsPage } from "@/features/clients/ClientDetailsPage";
import { PaymentsPage } from "@/features/payments/PaymentsPage";
import { PaymentDetailsPage } from "@/features/payments/PaymentDetailsPage";
import { WalletsPage } from "@/features/wallets/WalletsPage";
import { AdSpendPage } from "@/features/ad-spend/AdSpendPage";
import { TasksPage } from "@/features/tasks/TasksPage";
import { TaskDetailsPage } from "@/features/tasks/TaskDetailsPage";
import { ExpensesPage } from "@/features/expenses/ExpensesPage";
import { SubscriptionsPage } from "@/features/subscriptions/SubscriptionsPage";
import { TeamPage } from "@/features/team/TeamPage";
import { TeamProfilePage } from "@/features/team/TeamProfilePage";
import { ProfilePage } from "@/features/profile/ProfilePage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { ArchivesPage } from "@/features/archives/ArchivesPage";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { useAuthStore } from "@/features/auth/auth.store";
import { isAgencyAdmin, MOCK_USER } from "@/lib/permissions";

/** Redirects to /dashboard for admins, /profile for members. Falls back to MOCK_USER during offline/mock mode. */
function RoleAwareIndex() {
  const status = useAuthStore((s) => s.status);
  const { data: currentUser } = useCurrentUser();
  const user = currentUser ?? (status === "anonymous" ? MOCK_USER : null);
  if (user && isAgencyAdmin(user)) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/profile" replace />;
}

/** Blocks access to Dashboard for non-admin users. */
function DashboardGuard() {
  const status = useAuthStore((s) => s.status);
  const { data: currentUser } = useCurrentUser();
  const user = currentUser ?? (status === "anonymous" ? MOCK_USER : null);
  if (user && !isAgencyAdmin(user)) return <Navigate to="/profile" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <RoleAwareIndex /> },
      {
        element: <DashboardGuard />,
        children: [{ path: "dashboard", element: <DashboardPage /> }],
      },
      { path: "clients", element: <ClientsPage /> },
      { path: "clients/:clientId", element: <ClientDetailsPage /> },
      { path: "payments", element: <PaymentsPage /> },
      { path: "payments/:paymentId", element: <PaymentDetailsPage /> },
      { path: "wallets", element: <WalletsPage /> },
      { path: "ad-spend", element: <AdSpendPage /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "tasks/:taskId", element: <TaskDetailsPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "expenses", element: <ExpensesPage /> },
      { path: "subscriptions", element: <SubscriptionsPage /> },
      { path: "team", element: <TeamPage /> },
      { path: "team/:memberId", element: <TeamProfilePage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "archives", element: <ArchivesPage /> },
      { path: "*", element: <RoleAwareIndex /> },
    ],
  },
]);
